import find from 'lodash/find';
import flattenDeep from 'lodash/flattenDeep';
import forEach from 'lodash/forEach';
import isNumber from 'lodash/isNumber';
import map from 'lodash/map';
import some from 'lodash/some';
import {APIRegistry} from '@spothero/utils/api';
import EnvironmentUtils from '@spothero/utils/environment';
import {getSpots} from 'store/spots/spots-selectors';

const isBrowser = EnvironmentUtils.isBrowser();
const store = isBrowser ? require('store/store') : null;

const updateSpots = (data, spots, destinations, isDriving) => {
    for (let i = 0; i < data.length; i++) {
        const {distance, duration, status} = data[i];

        if (status !== 'NO_DATA' && isNumber(distance) && isNumber(duration)) {
            destinations[i].distanceInMiles = distance / 5280;
            destinations[i].durationInSeconds = duration;
        } else {
            destinations.splice(i, 1);
        }
    }

    forEach(destinations, destination => {
        const spot = find(spots, {parking_spot_id: destination.spotId}); // eslint-disable-line camelcase

        if (spot) {
            spot.distanceInMiles = destination.distanceInMiles;

            if (isDriving) {
                spot.drivingDuration = destination.durationInSeconds;
            } else {
                spot.walkingDuration = destination.durationInSeconds;
            }

            spot.hasTravelDistanceUpdated = true;
        }
    });

    return spots;
};

const makePromise = (spots, origin, destinations, isDriving) => {
    const travelMode = isDriving ? 'DRIVING' : 'WALKING';
    const destParams = flattenDeep(
        map(destinations, destination => {
            return destination.latlng;
        }),
        latlng => {
            return latlng;
        }
    ).join('&destinations=');

    return new Promise((resolve, reject) => {
        APIRegistry.get('distance')
            .get(
                `/v1/distance/?origins=${origin}&unitSystem=IMPERIAL&travelMode=${travelMode}&destinations=${destParams}`
            )
            .then(({data: {data}}) => {
                resolve(updateSpots(data, spots, destinations, isDriving));
            })
            .catch(response => {
                reject(
                    new Error(
                        `No results returned from the Distance Matrix Service. Response: "${response}".`
                    )
                );
            });
    });
};

export const retrieveSpotTravelDistance = spot => {
    const spots = [spot];
    const {
        searchRequest: {latitude, longitude},
    } = store.getStore().getState();
    const origin = `${latitude},${longitude}`;
    const isDriving = some(spot.selectedRate?.amenities, {slug: 'shuttle'});
    const destinations = [
        {
            latlng: `${spot.wd_latitude},${spot.wd_longitude}`,
            spotId: spot.parking_spot_id,
        },
    ];

    return makePromise(spots, origin, destinations, isDriving);
};

export const retrieveAirportSpotTravelDistance = spot => {
    const spots = [spot];
    const {
        facility: {
            airport: {
                details: {latitude, longitude},
            },
        },
    } = spot;
    const origin = `${spot.wd_latitude},${spot.wd_longitude}`;
    const isDriving = true; // TODO: Update this when new shuttle types are built out
    const destinations = [
        {
            latlng: `${latitude},${longitude}`,
            spotId: spot.parking_spot_id,
        },
    ];

    return makePromise(spots, origin, destinations, isDriving);
};

export const retrieveTravelDistances = (limit = 10) => {
    const storeState = store.getStore().getState();
    const spots = getSpots(storeState);
    const {
        searchRequest: {latitude, longitude},
    } = storeState;
    const origin = `${latitude},${longitude}`;
    const walkingDestinations = [];
    const drivingDestinations = [];
    const requests = [];
    let counter = 0;

    forEach(spots, spot => {
        if (counter < limit && !spot.hasTravelDistanceUpdated) {
            const selectedArray = some(spot.selectedRate.amenities, {
                slug: 'shuttle',
            })
                ? drivingDestinations
                : walkingDestinations;

            selectedArray.push({
                latlng: `${spot.wd_latitude},${spot.wd_longitude}`,
                spotId: spot.parking_spot_id,
            });

            counter++;
        }
    });

    if (walkingDestinations.length) {
        requests.push(makePromise(spots, origin, walkingDestinations));
    }

    if (drivingDestinations.length) {
        requests.push(makePromise(spots, origin, drivingDestinations, true));
    }

    return requests;
};

export const parseWalkingDuration = duration => {
    const minutes = Math.floor(duration / 60);
    const seconds = duration - minutes * 60;
    let display = minutes ? `${minutes} min` : `${seconds} sec`;

    if (minutes && seconds) {
        display = `${display}, ${seconds} sec`;
    }

    return display;
};
